import WorkspacesService from "../../infrastructure/Services/WorkspacesService";

import { currentSceneLang, localizedThumbnailUrl } from "./sceneI18n";

/**
 * Las escenas pre-armadas del picker (Coming Soon / BRB / Ending) no tienen
 * captura: el `.jpg` del thumbnail lo escribe el Assembler solo cuando una
 * escena está ACTIVA (captura el frame vivo). Por eso esas miniaturas salen
 * rotas en la tira de "Escenas".
 *
 * Acá sembramos ese mismo archivo usando la imagen que ya generamos para cada
 * escena (`scene-thumbnails/<lang>/<slug>.webp` en Garage): la bajamos, la
 * pasamos a JPEG y la escribimos en EL MISMO PATH donde el Assembler guarda los
 * screenshots — `${workspace}/scenes/<model.id>.jpg` (servido por el protocolo
 * `eriol-workspace://scenes/<model.id>.jpg`, que el `<img>` del Thumbnail ya
 * lee). Cuando el user active la escena, la captura real pisa este seed.
 *
 * Requiere `model.id` único por escena (lo da `buildStudioFromSceneTemplate`)
 * para que los tres `.jpg` no colisionen. Best-effort: cualquier fallo (sin
 * Electron, sin CORS, sin workspace) se ignora y la miniatura simplemente
 * queda rota como antes.
 */

/** Tamaño de las miniaturas de la tira de escenas (sembradas desde Garage y capturadas). */
export const SCENE_THUMB_WIDTH = 640;

export const SCENE_THUMB_HEIGHT = 360;

const THUMB_W = SCENE_THUMB_WIDTH;

const THUMB_H = SCENE_THUMB_HEIGHT;

/** Baja una imagen (AVIF) y la transcodifica a bytes JPEG vía canvas. */

const FETCH_TIMEOUT_MS = 8000;

const fetchAsJpegBytes = async (url: string): Promise<Uint8Array | null> => {
  // Timeout acotado: este fetch está dentro del flujo awaited de

  // SceneInitializer.initialize() → sin esto, un Garage/CDN colgado bloquearía

  // la inicialización del dashboard indefinidamente.

  const controller = new AbortController();

  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  // Best-effort (docstring): un fetch rechazado (timeout/abort, CORS, red)

  // devuelve null acá en vez de propagar — el caller sigue con los demás seeds.

  let res: Response;

  try {
    res = await fetch(url, { mode: "cors", signal: controller.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) return null;

  // createImageBitmap desde un Blob es same-origin (blob:) → no taintea el

  // canvas. El requisito CORS es poder leer el body del fetch; si CORS falla,

  // el fetch rechaza ANTES de llegar acá (no es un problema de tainting).

  const bitmap = await createImageBitmap(await res.blob());

  try {
    const canvas = document.createElement("canvas");

    canvas.width = THUMB_W;

    canvas.height = THUMB_H;

    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    ctx.drawImage(bitmap, 0, 0, THUMB_W, THUMB_H);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.85),
    );

    if (!blob) return null;

    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    bitmap.close();
  }
};

export interface SceneThumbSeed {
  /** `model.id` de la escena (== nombre del archivo `<id>.jpg`). */

  modelId: string;

  /** `imageUrl` del template (AVIF en `es`); se localiza al idioma activo. */

  imageUrl?: string;
}

/**
 * Escribe `${workspace}/scenes/<modelId>.jpg` para cada escena con thumbnail
 * disponible. No lanza: registra y sigue ante cualquier error.
 */

export const seedSceneThumbnails = async (
  seeds: SceneThumbSeed[],
): Promise<void> => {
  const api = window.electronAPI;

  if (!api?.saveFile || !api?.ensureDir) return;

  const withImage = seeds.filter((s) => Boolean(s.imageUrl));

  if (withImage.length === 0) return;

  let workspace: string | null = null;

  try {
    workspace = await new WorkspacesService("api").getCurrent();
  } catch {
    return;
  }

  if (!workspace) return;

  // Separador `/` (cross-platform): con `\\` en macOS el backslash es un

  // carácter LITERAL del nombre y el jpg caía fuera de scenes/ (thumbnail negro).

  const scenesDir = `${workspace}/scenes`;

  try {
    await api.ensureDir(scenesDir);
  } catch {
    return;
  }

  const lang = currentSceneLang();

  await Promise.all(
    withImage.map(async ({ modelId, imageUrl }) => {
      try {
        // Hardening: el archivo se nombra por modelId y se lee por el mismo id

        // (eriol-workspace://scenes/<modelId>.jpg). El saveFile del main no valida

        // path traversal, así que sólo sembramos ids con forma segura (los reales

        // son `scene-<slug>` kebab); cualquier otro se omite (queda roto, sin

        // riesgo de escribir fuera de scenes/).

        if (!/^[a-zA-Z0-9_-]+$/.test(modelId)) return;

        const url = localizedThumbnailUrl(imageUrl, lang);

        if (!url) return;

        const bytes = await fetchAsJpegBytes(url);

        if (!bytes) return;

        const result = await api.saveFile(
          `${scenesDir}/${modelId}.jpg`,

          bytes,
        );

        if (!result?.success) {
          throw new Error(result?.error ?? "saveFile devolvió success=false");
        }
      } catch (error) {
        console.warn(
          `[seedSceneThumbnails] no se pudo sembrar ${modelId}:`,

          error,
        );
      }
    }),
  );
};

/** Bytes JPEG (calidad 0.85) de un canvas ya dibujado. */
export const canvasToJpegBytes = async (
  canvas: HTMLCanvasElement,
): Promise<Uint8Array | null> => {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.85),
  );

  if (!blob) return null;

  return new Uint8Array(await blob.arrayBuffer());
};

/** Sólo ids con forma segura: el id viaja al main dentro de un path (saveFile no valida traversal). */
export const isSafeSceneId = (modelId: string): boolean =>
  /^[a-zA-Z0-9_-]+$/.test(modelId);

/**
 * Escribe `${workspace}/scenes/<modelId>.jpg` — lo que lee `eriol-workspace://scenes/<modelId>.jpg`
 * en la tira de escenas. Lo usa la captura fuera de pantalla de las escenas sin miniatura
 * (Radio.captureSceneThumbnail). `false` si no hay Electron/workspace, el id no es seguro o el
 * main no pudo escribir.
 */
export const writeSceneThumbnail = async (
  modelId: string,
  bytes: Uint8Array,
): Promise<boolean> => {
  const api = window.electronAPI;

  if (!api?.saveFile || !api?.ensureDir) return false;

  if (!isSafeSceneId(modelId)) return false;

  let workspace: string | null = null;

  try {
    workspace = await new WorkspacesService("api").getCurrent();
  } catch {
    return false;
  }

  if (!workspace) return false;

  // Separador `/` (cross-platform): con `\\` en macOS el backslash es un carácter LITERAL del
  // nombre y el jpg caía fuera de scenes/ (thumbnail negro).
  const scenesDir = `${workspace}/scenes`;

  try {
    await api.ensureDir(scenesDir);

    const result = await api.saveFile(`${scenesDir}/${modelId}.jpg`, bytes);

    return Boolean(result?.success);
  } catch {
    return false;
  }
};
